import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsQR from "jsqr";
import {
  Camera,
  ClipboardList,
  Keyboard,
  RotateCcw,
  ScanLine,
  Truck,
  VideoOff,
} from "lucide-react";
import ActionButton from "../../components/ui/ActionButton";
import { Card } from "../../components/ui/Card";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeading } from "../../components/ui/PageHeading";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import PlantValue from "../../components/ui/PlantValue";
import QRCodePreview from "../../components/ui/QRCodePreview";
import {
  useDispatchBatchMutation,
  useDownloadDispatchSlipMutation,
} from "../../features/batches/batchApi";
import { useLazyGetOemsQuery } from "../../features/oems/oemApi";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import { useVerifyQrMutation } from "../../features/qr/qrApi";
import {
  assertSuccessfulResponse,
  getRequestErrorMessage,
  getResponseMessage,
} from "../../services/apiMessages";
import { downloadBlob } from "../../services/fileDownload";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { buildNameCodeIdLabel } from "../masters/masterMetadata";
import ProgressBatchDetails from "./ProgressBatchDetails";

const HIDDEN_RECORD_FIELDS = new Set(["qr_code_base64"]);
const INVALID_QR_MESSAGE =
  "Invalid QR. QR must contain exactly two values in value1_value2 format.";

const humanizeKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const renderPrimitiveValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const renderRecordValue = (value) => {
  if (React.isValidElement(value)) return value;

  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) return "-";

    return (
      <div className="grid gap-1 rounded-md bg-white px-3 py-2">
        {entries.map(([key, itemValue]) => (
          <div key={key} className="grid gap-1 sm:grid-cols-[120px_1fr]">
            <span className="text-slate-500">{humanizeKey(key)}</span>
            <span className="break-all font-medium text-slate-900">
              {renderPrimitiveValue(itemValue)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return renderPrimitiveValue(value);
};

const getVerificationData = (response) => response?.data?.[0] || null;

const parseQrValue = (value) => {
  const normalizedValue = String(value || "").trim();
  const parts = normalizedValue.split("_");

  if (parts.length !== 2 || parts.some((part) => !part.trim())) {
    return { isValid: false, normalizedValue, parts: [] };
  }

  return {
    isValid: true,
    normalizedValue,
    parts: parts.map((part) => part.trim()),
  };
};

const getItemDisplay = (record) => {
  const item = record?.item_detail;

  return buildNameCodeIdLabel({
    name: item?.item_name,
    code: item?.item_code,
    id: item?.item_id,
    fallbackId: record?.item_id,
  });
};

const getOemOptionLabel = (oem, fallbackOemId) => {
  return buildNameCodeIdLabel({
    name: oem?.oem_name,
    code: oem?.oem_code,
    id: oem?.oem_id,
    fallbackId: fallbackOemId,
  });
};

const getDispatchStatus = (record) =>
  record?.dispatch_on ? "Dispatched" : "Pending";

const getBatchUid = (record) => record?.uid || record?._id || record?.id;
const getDispatchSlipFilename = (record) =>
  `dispatch-slip-${record?.batch_id || getBatchUid(record) || "batch"}.pdf`;

const getPlantDetailFromMeta = (metaData, plantId) => {
  if (!plantId) return null;

  return metaData?.plant_details?.[String(plantId)] || null;
};

const getItemDetailFromMeta = (metaData, plantId, itemId) => {
  if (!plantId || !itemId) return null;

  return metaData?.item_details?.[`${plantId}:${itemId}`] || null;
};

const getOemDetailFromMeta = (metaData, plantId, oemId) => {
  if (!plantId || !oemId) return null;

  return metaData?.oem_details?.[`${plantId}:${oemId}`] || null;
};

const buildDispatchRecord = (batchRecord, metaData) => {
  if (!batchRecord) return {};

  return {
    ...batchRecord,
    plant_detail:
      batchRecord.plant_detail ||
      getPlantDetailFromMeta(metaData, batchRecord.plant_id),
    item_detail:
      batchRecord.item_detail ||
      getItemDetailFromMeta(metaData, batchRecord.plant_id, batchRecord.item_id),
    oem_detail:
      batchRecord.oem_detail ||
      getOemDetailFromMeta(metaData, batchRecord.plant_id, batchRecord.dispatch_to),
  };
};

const buildDispatchResult = (qrValue, batchRecord, metaData = {}) => ({
  qr_value: batchRecord?.qr_value || qrValue,
  entity_type: "batch",
  plant_id: batchRecord?.plant_id || "",
  entity_id: batchRecord?.batch_id || "",
  record: buildDispatchRecord(batchRecord, metaData),
});

const Scanner = () => {
  const [activeTab, setActiveTab] = useState("progress");
  const [scanMode, setScanMode] = useState("text");
  const { selectedPlantId } = usePlantSelection();
  const [entityIdText, setEntityIdText] = useState("");
  const [cameraQrValue, setCameraQrValue] = useState("");
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [result, setResult] = useState(null);
  const [selectedDispatchTo, setSelectedDispatchTo] = useState("");
  const [hasJustDispatched, setHasJustDispatched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDispatchConfirmOpen, setIsDispatchConfirmOpen] = useState(false);
  const [verifyQr, { isLoading: isVerifying }] = useVerifyQrMutation();
  const [dispatchBatch, { isLoading: isDispatching }] = useDispatchBatchMutation();
  const [downloadDispatchSlip, { isLoading: isDownloadingSlip }] =
    useDownloadDispatchSlipMutation();
  const [loadOems, { data: oemData, isFetching: isLoadingOems }] =
    useLazyGetOemsQuery();
  const toast = useToast();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameRequestRef = useRef(null);
  const streamRef = useRef(null);
  const cameraSessionRef = useRef(0);
  const cameraStartTimeoutRef = useRef(null);
  const lastScannedValueRef = useRef("");
  const verificationRequestRef = useRef(0);

  const textQrValue = useMemo(() => {
    const entityId = entityIdText.trim();

    return selectedPlantId && entityId ? `${selectedPlantId}_${entityId}` : "";
  }, [entityIdText, selectedPlantId]);

  const activeQrValue = scanMode === "text" ? textQrValue : cameraQrValue;
  const activeQrValidation = useMemo(
    () => parseQrValue(activeQrValue),
    [activeQrValue]
  );
  const hasCompleteTextInput = Boolean(selectedPlantId && entityIdText.trim());
  const textInputIsInvalid = hasCompleteTextInput && !activeQrValidation.isValid;

  const record = result?.record || null;
  const isBatch = result?.entity_type === "batch";
  const isAlreadyDispatched = Boolean(record?.dispatch_on);
  const dispatchOemOptions = useMemo(() => {
    const plantId = result?.plant_id || record?.plant_id;
    const options = [];
    const optionValues = new Set();

    (oemData?.data || []).forEach((oem) => {
      if (!oem?.oem_id) return;
      if (plantId && String(oem.plant_id) !== String(plantId)) return;

      const value = String(oem.oem_id);
      options.push({
        value,
        label: getOemOptionLabel(oem, value),
      });
      optionValues.add(value);
    });

    const fallbackValue = selectedDispatchTo || record?.dispatch_to;
    if (fallbackValue && !optionValues.has(String(fallbackValue))) {
      options.unshift({
        value: String(fallbackValue),
        label: getOemOptionLabel(record?.oem_detail, String(fallbackValue)),
      });
    }

    return options;
  }, [
    oemData?.data,
    record?.plant_id,
    record?.dispatch_to,
    record?.oem_detail,
    result?.plant_id,
    selectedDispatchTo,
  ]);
  const selectedDispatchOemLabel = useMemo(() => {
    const selectedOption = dispatchOemOptions.find(
      (option) => option.value === selectedDispatchTo
    );

    return (
      selectedOption?.label ||
      getOemOptionLabel(record?.oem_detail, selectedDispatchTo || record?.dispatch_to)
    );
  }, [dispatchOemOptions, record?.dispatch_to, record?.oem_detail, selectedDispatchTo]);
  const cameraDeviceOptions = useMemo(
    () =>
      videoDevices.map((device, index) => ({
        value: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      })),
    [videoDevices]
  );
  const hasDispatchTo = Boolean(selectedDispatchTo);
  const dispatchNotice = useMemo(() => {
    if (!result) return "";
    if (!isBatch) return "Only batch QR codes can be dispatched.";
    if (isAlreadyDispatched) {
      return hasJustDispatched ? "Dispatched." : "Batch is already dispatched.";
    }
    if (!hasDispatchTo) return "Dispatch To OEM is required before dispatch.";
    return "";
  }, [hasDispatchTo, hasJustDispatched, isAlreadyDispatched, isBatch, result]);

  const canDispatch =
    Boolean(result) &&
    isBatch &&
    hasDispatchTo &&
    !isAlreadyDispatched &&
    !isDispatching;
  const canDownloadDispatchSlip =
    Boolean(result) &&
    isBatch &&
    Boolean(getBatchUid(record)) &&
    isAlreadyDispatched &&
    !isDownloadingSlip;
  const dispatchNoticeClassName =
    hasJustDispatched && isAlreadyDispatched
      ? "bg-green-50 text-green-700"
      : "bg-amber-50 text-amber-700";

  const recordEntries = useMemo(() => {
    if (!record) return [];

    return Object.entries(record).filter(
      ([key]) => !HIDDEN_RECORD_FIELDS.has(key)
    );
  }, [record]);

  const summaryItems = useMemo(
    () => [
      ["Entity Type", result?.entity_type],
      ["Batch ID", result?.entity_id],
      [
        "Plant",
        <PlantValue
          plant={record?.plant_detail}
          fallbackPlantId={result?.plant_id || record?.plant_id}
        />,
      ],
      ["Item", getItemDisplay(record)],
      ["Dispatch To", selectedDispatchOemLabel],
      ["Dispatch Status", getDispatchStatus(record)],
      ["Dispatch By", record?.dispatch_by],
    ],
    [record, result, selectedDispatchOemLabel]
  );

  const stopCamera = useCallback(() => {
    cameraSessionRef.current += 1;
    window.clearTimeout(cameraStartTimeoutRef.current);
    if (frameRequestRef.current) {
      window.cancelAnimationFrame(frameRequestRef.current);
      frameRequestRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
  }, []);

  const verifyValue = useCallback(
    async (value) => {
      const requestId = ++verificationRequestRef.current;
      const validation = parseQrValue(value);

      if (!validation.normalizedValue || !validation.isValid) {
        setErrorMessage(INVALID_QR_MESSAGE);
        setResult(null);
        setSelectedDispatchTo("");
        setHasJustDispatched(false);
        return;
      }

      if (!selectedPlantId || validation.parts[0] !== String(selectedPlantId)) {
        setErrorMessage("Select the matching plant in the top bar before scanning this batch.");
        setResult(null);
        return;
      }

      setResult(null);
      setErrorMessage("");
      try {
        const response = assertSuccessfulResponse(
          await verifyQr({ qr_value: validation.normalizedValue }).unwrap(),
          "Unable to verify QR code."
        );
        if (requestId !== verificationRequestRef.current) return;
        const nextResult = getVerificationData(response);
        const nextDispatchTo = nextResult?.record?.dispatch_to || "";

        setResult(nextResult);
        setSelectedDispatchTo(nextDispatchTo ? String(nextDispatchTo) : "");
        setHasJustDispatched(false);
        setCameraError("");
        if (nextResult?.entity_type !== "batch") {
          setErrorMessage("A batch QR code is required for this flow.");
          return;
        }

        setErrorMessage("");
        toast.success(getResponseMessage(response, "QR verified successfully."));
      } catch (error) {
        if (requestId !== verificationRequestRef.current) return;
        setResult(null);
        setSelectedDispatchTo("");
        setHasJustDispatched(false);
        setErrorMessage(
          getRequestErrorMessage(error, "Unable to verify QR code.")
        );
      }
    },
    [selectedPlantId, toast, verifyQr]
  );

  const loadVideoDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === "videoinput");

    setVideoDevices(cameras);
    return cameras;
  }, []);

  const resetDispatchScan = useCallback(() => {
    verificationRequestRef.current += 1;
    stopCamera();
    setEntityIdText("");
    setCameraQrValue("");
    setCameraStatus("");
    setCameraError("");
    setErrorMessage("");
    setResult(null);
    setSelectedDispatchTo("");
    setHasJustDispatched(false);
    setIsDispatchConfirmOpen(false);
    lastScannedValueRef.current = "";
  }, [stopCamera]);

  useEffect(() => {
    resetDispatchScan();
  }, [selectedPlantId, resetDispatchScan]);

  const scanVideoFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !streamRef.current) {
      return;
    }

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (width && height) {
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d", { willReadFrequently: true });

        if (context) {
          context.drawImage(video, 0, 0, width, height);

          const imageData = context.getImageData(0, 0, width, height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          const scannedText = code?.data?.trim();

          if (scannedText && scannedText !== lastScannedValueRef.current) {
            const validation = parseQrValue(scannedText);

            lastScannedValueRef.current = scannedText;
            setCameraQrValue(scannedText);
            stopCamera();

            if (!validation.isValid) {
              setCameraStatus("Invalid QR captured.");
              setErrorMessage(INVALID_QR_MESSAGE);
              setResult(null);
              setSelectedDispatchTo("");
              setHasJustDispatched(false);
              return;
            }

            setCameraStatus("QR captured.");
            verifyValue(scannedText);
            return;
          }
        }
      }
    }

    frameRequestRef.current = window.requestAnimationFrame(scanVideoFrame);
  }, [stopCamera, verifyValue]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera access is not supported in this browser.");
      return;
    }

    verificationRequestRef.current += 1;
    stopCamera();
    const cameraSession = cameraSessionRef.current;
    setCameraQrValue("");
    setCameraError("");
    setErrorMessage("");
    setResult(null);
    setSelectedDispatchTo("");
    setHasJustDispatched(false);
    setCameraStatus("Opening camera...");
    lastScannedValueRef.current = "";

    try {
      const devices = videoDevices.length ? videoDevices : await loadVideoDevices();
      if (cameraSession !== cameraSessionRef.current) return;
      const preferredDevice =
        selectedDeviceId ||
        devices.find((device) => /back|rear|environment/i.test(device.label))
          ?.deviceId ||
        devices[0]?.deviceId;
      const constraints = {
        audio: false,
        video: preferredDevice
          ? { deviceId: { exact: preferredDevice } }
          : { facingMode: { ideal: "environment" } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (cameraSession !== cameraSessionRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      if (cameraSession !== cameraSessionRef.current) return;

      await loadVideoDevices();
      if (cameraSession !== cameraSessionRef.current) return;
      if (!selectedDeviceId && preferredDevice) {
        setSelectedDeviceId(preferredDevice);
      }

      setIsCameraActive(true);
      setCameraStatus("Scanning...");
      frameRequestRef.current = window.requestAnimationFrame(scanVideoFrame);
    } catch (error) {
      if (cameraSession !== cameraSessionRef.current) return;
      stopCamera();
      setCameraStatus("");
      setCameraError(
        error?.message || "Unable to access the camera. Check browser permission."
      );
    }
  }, [
    loadVideoDevices,
    scanVideoFrame,
    selectedDeviceId,
    stopCamera,
    videoDevices,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasCompleteTextInput) {
      setErrorMessage("Select a plant from the top bar and enter a batch ID.");
      setResult(null);
      setSelectedDispatchTo("");
      setHasJustDispatched(false);
      return;
    }

    await verifyValue(textQrValue);
  };

  const handleModeChange = (mode) => {
    if (mode === scanMode) return;
    verificationRequestRef.current += 1;
    setScanMode(mode);
    setResult(null);
    setErrorMessage("");
    setCameraError("");

    if (mode === "text") {
      stopCamera();
      return;
    }

    cameraStartTimeoutRef.current = window.setTimeout(() => {
      startCamera();
    }, 0);
  };

  const handleDeviceChange = (value) => {
    setSelectedDeviceId(value);
    if (isCameraActive) {
      stopCamera();
    }
  };

  const handleConfirmDispatch = async () => {
    if (!result?.qr_value) return;
    if (!selectedDispatchTo) {
      setErrorMessage("Dispatch To OEM is required before dispatch.");
      setIsDispatchConfirmOpen(false);
      return;
    }

    try {
      const response = assertSuccessfulResponse(
        await dispatchBatch({
          qr_value: result.qr_value,
          dispatch_to: selectedDispatchTo,
        }).unwrap(),
        "Unable to dispatch batch."
      );
      const batchRecord = response?.data?.[0] || null;

      setResult(buildDispatchResult(result.qr_value, batchRecord, response?.meta_data));
      setSelectedDispatchTo(
        batchRecord?.dispatch_to ? String(batchRecord.dispatch_to) : selectedDispatchTo
      );
      setHasJustDispatched(true);
      setErrorMessage("");
      setIsDispatchConfirmOpen(false);
      toast.success(getResponseMessage(response, "Batch dispatched successfully."));
    } catch (error) {
      setErrorMessage(getRequestErrorMessage(error, "Unable to dispatch batch."));
      setIsDispatchConfirmOpen(false);
    }
  };

  const handleDownloadDispatchSlip = async () => {
    const uid = getBatchUid(record);
    if (!uid || !record?.dispatch_on) return;

    try {
      const blob = await downloadDispatchSlip(uid).unwrap();
      downloadBlob(blob, getDispatchSlipFilename(record));
      toast.success("Dispatch slip downloaded.");
    } catch (error) {
      setErrorMessage("Unable to download dispatch slip.");
    }
  };

  const handleProgressChanged = (response) => {
    const updatedRecord = Array.isArray(response?.data) ? response.data[0] : response?.data || response;
    if (!updatedRecord || !result) return;
    setResult({
      ...result,
      record: buildDispatchRecord({ ...result.record, ...updatedRecord }, response?.meta_data),
    });
  };

  useEffect(() => {
    if (scanMode === "camera") {
      loadVideoDevices().catch(() => {
        setVideoDevices([]);
      });
    }
  }, [loadVideoDevices, scanMode]);

  useEffect(() => {
    if (activeTab !== "dispatch" || !isBatch || !result?.plant_id || oemData || isLoadingOems) return;

    loadOems({ page: 1, limit: 1000 });
  }, [activeTab, isBatch, isLoadingOems, loadOems, oemData, result?.plant_id]);

  useEffect(() => () => {
    verificationRequestRef.current += 1;
    stopCamera();
  }, [stopCamera]);

  const showVisualQr = Boolean(
    activeQrValue && (scanMode === "camera" || activeQrValidation.isValid)
  );
  const statusMessage =
    errorMessage ||
    cameraError ||
    (scanMode === "text" && textInputIsInvalid ? INVALID_QR_MESSAGE : "") ||
    cameraStatus ||
    "";
  const dispatchSelectPlaceholder = (() => {
    if (!result) return "Scan batch first";
    if (!isBatch) return "Batch QR required";
    if (isLoadingOems && dispatchOemOptions.length === 0) return "Loading OEMs...";
    if (dispatchOemOptions.length === 0) return "No OEMs found";
    return "Select dispatch location";
  })();
  const dispatchSelectDisabled =
    !result ||
    !isBatch ||
    isAlreadyDispatched ||
    (isLoadingOems && dispatchOemOptions.length === 0);

  return (
    <div className="flex min-h-[calc(100dvh-92px)] flex-col px-3 pb-5 sm:px-5">
      <PageHeading title="Scanner" className="mb-3 shrink-0" />

      <div className="mb-3 grid shrink-0 grid-cols-2 rounded-lg bg-slate-100 p-1 sm:max-w-md">
        {[
          ["progress", ClipboardList, "Progress Log"],
          ["dispatch", Truck, "Dispatch"],
        ].map(([tab, Icon, label]) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex min-h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold transition ${
              activeTab === tab
                ? "bg-white text-primary shadow-sm"
                : "text-slate-600 hover:text-primary"
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{label}</span>
          </button>
        ))}
      </div>

        <div className="grid flex-1 grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(320px,440px)_1fr]">
          <Card className={`${activeTab === "progress" && isBatch ? "hidden lg:flex" : "flex"} min-h-[540px] flex-col overflow-hidden p-3 sm:p-4`}>
            <div className="grid shrink-0 grid-cols-2 rounded-lg bg-slate-100 p-1">
              {[
                ["text", Keyboard, "Text"],
                ["camera", Camera, "Camera"],
              ].map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleModeChange(mode)}
                  className={`flex min-h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold transition ${
                    scanMode === mode
                      ? "bg-white text-primary shadow-sm"
                      : "text-slate-600 hover:text-primary"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {!selectedPlantId && <p className="mt-3 text-sm text-amber-700">Select a plant from the top bar to scan a batch.</p>}
            <form className="mt-3 shrink-0" onSubmit={handleSubmit}>
              <div className="grid min-h-[70px] grid-cols-1 gap-3">
                {scanMode === "text" ? (
                  <>
                    <div className="min-w-0">
                      <Label htmlFor="scanner_entity_id">Batch ID</Label>
                      <Input
                        id="scanner_entity_id"
                        value={entityIdText}
                        onChange={(event) => {
                          verificationRequestRef.current += 1;
                          setEntityIdText(event.target.value);
                          setErrorMessage("");
                          setResult(null);
                          setSelectedDispatchTo("");
                          setHasJustDispatched(false);
                        }}
                        placeholder="Enter batch ID"
                        autoComplete="off"
                      />
                    </div>
                  </>
                ) : (
                  <div className="min-w-0 sm:col-span-2">
                    <Label htmlFor="scanner_camera_device">Camera</Label>
                    <PaginatedSelect
                      value={selectedDeviceId}
                      onChange={handleDeviceChange}
                      options={cameraDeviceOptions}
                      disabled={isCameraActive}
                      placeholder="Default camera"
                    />
                  </div>
                )}
              </div>

              {scanMode === "text" && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <ActionButton
                    action="confirm"
                    type="submit"
                    isLoading={isVerifying}
                    disabled={!hasCompleteTextInput || textInputIsInvalid}
                    label="Fetch Details"
                    className="w-full"
                  />
                  <ActionButton
                    action="cancel"
                    icon={RotateCcw}
                    variant="outline"
                    onClick={resetDispatchScan}
                    label="Reset"
                    className="w-full"
                  />
                </div>
              )}
            </form>

            <div className="relative mt-3 min-h-[240px] flex-1 overflow-hidden rounded-xl bg-slate-950">
              {scanMode === "camera" && (
                <>
                  <video
                    ref={videoRef}
                    className={`absolute inset-0 h-full w-full object-cover ${
                      isCameraActive ? "opacity-100" : "opacity-0"
                    }`}
                    muted
                    playsInline
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  {isCameraActive && (
                    <div className="pointer-events-none absolute inset-6 rounded-xl border-2 border-white/80 shadow-[0_0_0_999px_rgba(15,23,42,0.35)]" />
                  )}
                </>
              )}

              {!showVisualQr && !(scanMode === "camera" && isCameraActive) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900 text-slate-300">
                  <ScanLine className="h-10 w-10" />
                  <span className="text-sm font-medium">
                    {scanMode === "camera" ? "Camera ready" : "QR preview"}
                  </span>
                </div>
              )}

              {showVisualQr && !(scanMode === "camera" && isCameraActive) && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-50 p-3">
                  <QRCodePreview
                    value={activeQrValue}
                    displayValue={activeQrValue}
                    label={activeTab === "progress" ? "Batch QR" : "Dispatch QR"}
                    size={128}
                    showValue
                    className="max-w-full"
                  />
                </div>
              )}
            </div>

            <div className="mt-3 grid h-10 shrink-0 grid-cols-1 items-center">
              {statusMessage && (
                <p
                  role={errorMessage || cameraError || textInputIsInvalid ? "alert" : "status"}
                  className={`rounded-md px-3 py-2 text-sm font-medium ${
                    errorMessage || cameraError || textInputIsInvalid
                      ? "bg-red-50 text-red-600"
                      : "bg-sky-50 text-sky-700"
                  }`}
                  title={statusMessage}
                >
                  {statusMessage}
                </p>
              )}
            </div>

            {scanMode === "camera" && (
              <div className="mt-3 grid shrink-0 grid-cols-2 gap-3">
                <ActionButton
                  action="confirm"
                  icon={Camera}
                  onClick={startCamera}
                  disabled={isCameraActive}
                  label={cameraQrValue ? "Recapture" : "Start"}
                  className="w-full"
                />
                <ActionButton
                  action="cancel"
                  icon={isCameraActive ? VideoOff : RotateCcw}
                  variant="outline"
                  onClick={isCameraActive ? stopCamera : resetDispatchScan}
                  label={isCameraActive ? "Stop" : "Reset"}
                  className="w-full"
                />
              </div>
            )}
          </Card>

          {activeTab === "progress" ? (
            <ProgressBatchDetails
              key={getBatchUid(record) || "empty"}
              result={result}
              onBatchChanged={handleProgressChanged}
              onScanNext={resetDispatchScan}
              isVerifying={isVerifying}
            />
          ) : (
          <Card className="flex min-h-[540px] flex-col overflow-hidden p-3 sm:p-4">
            <div className="flex h-8 shrink-0 items-center justify-between gap-2">
              <h2 className="truncate text-base font-semibold text-slate-900">
                Dispatch Details
              </h2>
              <span
                className={`inline-flex w-fit shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                  result
                    ? "bg-green-50 text-green-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {result ? "Loaded" : "Pending"}
              </span>
            </div>

            <div className="mt-3 grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-3">
              {summaryItems.map(([label, value]) => (
                <div
                  key={label}
                  className="min-w-0 rounded-lg bg-slate-50 px-3 py-2"
                >
                  <p className="truncate text-xs font-medium uppercase text-slate-500">
                    {label}
                  </p>
                  <div className="mt-1 min-w-0 text-sm font-semibold text-slate-900">
                    {renderRecordValue(value)}
                  </div>
                </div>
              ))}
            </div>

            {dispatchNotice && (
              <p className={`mt-3 rounded-md px-3 py-2 text-sm font-medium ${dispatchNoticeClassName}`}>
                {dispatchNotice}
              </p>
            )}

            <div className="mt-3 grid shrink-0 gap-3 sm:grid-cols-[minmax(220px,1fr)_auto] sm:items-end">
              <div className="min-w-0">
                <Label htmlFor="dispatch_to">Dispatch Location</Label>
                <PaginatedSelect
                  value={selectedDispatchTo}
                  onChange={(value) => {
                    setSelectedDispatchTo(value);
                    setErrorMessage("");
                  }}
                  options={dispatchOemOptions}
                  disabled={dispatchSelectDisabled}
                  placeholder={dispatchSelectPlaceholder}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                <ActionButton
                  action="confirm"
                  icon={Truck}
                  label="Dispatch Batch"
                  disabled={!canDispatch}
                  isLoading={isDispatching}
                  onClick={() => setIsDispatchConfirmOpen(true)}
                  className="w-full sm:w-auto"
                />
                <ActionButton
                  action="download"
                  label="Slip"
                  disabled={!canDownloadDispatchSlip}
                  isLoading={isDownloadingSlip}
                  onClick={handleDownloadDispatchSlip}
                  className="w-full sm:w-auto"
                />
              </div>
            </div>

            <div className="mt-3 min-h-[120px] flex-1 overflow-hidden rounded-lg bg-slate-50">
              {recordEntries.length === 0 ? (
                <div className="flex h-full items-center justify-center px-4 text-center text-sm text-slate-500">
                  No batch selected
                </div>
              ) : (
                <div className="max-h-[360px] overflow-y-auto">
                  {recordEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className="grid gap-2 border-b border-white px-3 py-2 text-sm last:border-b-0 sm:grid-cols-[minmax(140px,180px)_1fr]"
                    >
                      <span className="truncate font-medium text-slate-600">
                        {humanizeKey(key)}
                      </span>
                      <span className="min-w-0 break-all text-slate-900">
                        {renderRecordValue(value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
          )}
        </div>

      <ConfirmDialog
        open={isDispatchConfirmOpen}
        onOpenChange={setIsDispatchConfirmOpen}
        title="Dispatch batch?"
        description={`Batch ${result?.entity_id || ""} will be marked as dispatched to ${selectedDispatchOemLabel}.`}
        confirmLabel="Dispatch"
        onConfirm={handleConfirmDispatch}
        isLoading={isDispatching}
      />
    </div>
  );
};

export default Scanner;
